import React, { useEffect, useState } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { Formik, Form, FormikHelpers } from 'formik';

import { isAxiosError } from 'axios';
import SearchUserForm from './SearchUser';
import EditUser from './EditUser';
import UpdateSuccess from './UpdateSuccess';

import validationSchema from './validationSchema';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { Modal } from '../../../../global';

const steps = ['Search User', 'Modify User'];

interface UserData {
  username: string;
  reports: string[];
  roles: string[];
  email: string;
  firstName: string;
  lastName: string;
}
interface FormikValues {
  userName: string;
  password: string;
  confirmPassword: string;
  email: string;
  firstName: string;
  lastName: string;
}
const CheckoutPage = () => {
  const axiosPrivate = useAxiosPrivate();
  const [activeStep, setActiveStep] = useState(0);
  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;
  const [foundUser, setFoundUser] = useState<null | UserData>(null);
  const [reportList, setReportList] = useState([]);
  const [reportsSelected, setReportsSelected] = useState(
    foundUser?.reports || []
  );
  const [rolesSelected, setRolesSelected] = useState(foundUser?.roles || []);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    titleText: '',
    bodyText: '',
    autoClose: false,
  });

  useEffect(() => {
    if (foundUser) {
      setRolesSelected(foundUser.roles);
      setReportsSelected(foundUser.reports);
    }
  }, [foundUser]);

  const searchUser = async (username: string) => {
    try {
      const userResponse = await axiosPrivate.post('/select-user', {
        user: username,
      });

      const reportsResponse = await axiosPrivate.get('/get-all-reports');
      setReportList(reportsResponse.data);

      setFoundUser(userResponse.data);
      return 'User found';
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (error.response?.data === 'User Not Found') {
          return error.response?.data;
        }
        return 'Error finding User';
      }
      return 'Error finding User';
    }
  };

  const updateUser = async ({
    userName,
    password,
    firstName,
    lastName,
    email,
  }: {
    userName: string;
    password: string;
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    const response = await axiosPrivate.put('/update-user', {
      user: userName,
      newPassword: password,
      firstName,
      lastName,
      email,
      reportsSelected,
      rolesSelected,
    });
    if (response.status === 200) return 'Success';
    return 'Failed to update';
  };

  const submitForm = async (
    values: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    // prevents submitting if no changes were made
    if (
      values.firstName === '' &&
      values.lastName === '' &&
      values.email === '' &&
      values.password === '' &&
      reportsSelected === foundUser?.reports &&
      rolesSelected === foundUser?.roles
    ) {
      setLoading(false);
    } else {
      setLoading(true);
      const response = await updateUser(values);
      if (response === 'Success') {
        setLoading(false);
        actions.setSubmitting(false);
        setActiveStep(activeStep + 1);
      }
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  const handleSearchUserResponse = (
    axiosResponse: string,
    values: FormikValues
  ) => {
    if (axiosResponse === 'User found') {
      setActiveStep(activeStep + 1);
    }
    if (axiosResponse === 'User Not Found') {
      setModalInfo({
        titleText: 'No User Found!',
        bodyText: `User: ${values.userName} doesn't exist!`,
        autoClose: true,
      });
      setShowModal(true);
    }
    if (axiosResponse === 'Error finding User') {
      setModalInfo({
        titleText: 'User Search Failed!',
        bodyText:
          'An issue occurred while searching for user! Please try again.',
        autoClose: false,
      });
      setShowModal(true);
    }
  };

  // This runs after validation is successful
  const handleSubmit = async (
    values: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      if (activeStep === 0) {
        const outcome = await searchUser(values.userName);
        handleSearchUserResponse(outcome, values);
      }

      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  function renderStepContent(step: number) {
    if (loading) return <>loading</>;
    switch (step) {
      case 0:
        return <SearchUserForm />;
      case 1:
        return (
          <EditUser
            foundUser={foundUser as UserData}
            reportsSelected={reportsSelected}
            setReportsSelected={setReportsSelected}
            rolesSelected={rolesSelected}
            setRolesSelected={setRolesSelected}
            reportList={reportList}
          />
        );
      default:
        return <div>Not Found</div>;
    }
  }

  return (
    <>
      <Typography component="h1" variant="h4" align="center">
        Update User
      </Typography>
      {showModal && (
        <Modal
          titleText={modalInfo.titleText}
          bodyText={modalInfo.bodyText}
          autoClose={modalInfo.autoClose}
          updateParent={setShowModal}
        />
      )}
      <Stepper
        activeStep={activeStep}
        sx={{ marginY: 2.5, marginX: 'auto', maxWidth: '600px' }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {activeStep === steps.length ? (
        <UpdateSuccess setActiveStep={setActiveStep} />
      ) : (
        <Formik
          initialValues={{
            userName: '',
            password: '',
            confirmPassword: '',
            email: '',
            firstName: '',
            lastName: '',
          }}
          validationSchema={currentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form id="updateUserForm">
              {renderStepContent(activeStep)}

              <Box
                sx={{ display: 'flex', justifyContent: 'end', marginTop: 2 }}
              >
                {activeStep !== 0 && <Button onClick={handleBack}>Back</Button>}
                <Box>
                  <Button
                    disabled={isSubmitting}
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ marginLeft: 2 }}
                  >
                    {isLastStep ? 'Update User' : 'Next'}
                  </Button>
                  {isSubmitting && <CircularProgress size={24} />}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};
export default CheckoutPage;
